import * as React from 'react';

export interface PartDPenaltyCalculatorProps {
  indDob?: string;
  basePremium?: number;
}

function monthDiff(): number {
  const dateFrom = new Date(2006, '01' as any);
  const dateTo = new Date();
  return (
    dateTo.getMonth() -
    dateFrom.getMonth() +
    12 * (dateTo.getFullYear() - dateFrom.getFullYear()) +
    1
  );
}

function calculateByBirthday(
  bMonth: string,
  bYear: string,
  basePremium: number
): { months: number; penalty: number } {
  const monthInt = parseInt(bMonth, 10);
  const yearInt = parseInt(bYear, 10);
  if (!monthInt || !yearInt) {
    return { months: 0, penalty: 0 };
  }

  const dateTurned65 = new Date(yearInt + 65, monthInt);
  const now = new Date();

  let monthsWithoutCoverage =
    now.getMonth() -
    dateTurned65.getMonth() +
    12 * (now.getFullYear() - dateTurned65.getFullYear()) +
    2;

  if (monthsWithoutCoverage < 0) monthsWithoutCoverage = 0;

  const maxMonths = monthDiff();
  const monthsToUse = monthsWithoutCoverage < maxMonths ? monthsWithoutCoverage : maxMonths;

  let monthsToPercent = 0;
  if (monthsToUse > 0) {
    monthsToPercent = monthsToUse / 100;
  }

  const penalty = parseFloat((basePremium * monthsToPercent).toFixed(2));
  return { months: monthsToUse, penalty };
}

function calculateByLoss(
  bMonth: string,
  bYear: string,
  basePremium: number
): { months: number; penalty: number } {
  const monthInt = parseInt(bMonth, 10);
  const yearInt = parseInt(bYear, 10);
  if (!monthInt || !yearInt) {
    return { months: 0, penalty: 0 };
  }

  const dateOfLastCoverage = new Date(yearInt, monthInt + 1);
  const now = new Date();

  let monthsWithoutCoverage =
    now.getMonth() -
    dateOfLastCoverage.getMonth() +
    12 * (now.getFullYear() - dateOfLastCoverage.getFullYear()) +
    2;

  if (monthsWithoutCoverage < 0) monthsWithoutCoverage = 0;

  const maxMonths = monthDiff();
  const monthsToUse = monthsWithoutCoverage < maxMonths ? monthsWithoutCoverage : maxMonths;

  let monthsToPercent = 0;
  if (monthsToUse > 0) {
    monthsToPercent = monthsToUse / 100;
  }

  const penalty = parseFloat((basePremium * monthsToPercent).toFixed(1));
  return { months: monthsToUse, penalty };
}

// 2026 base premium = 38.99
export const PartDPenaltyCalculator: React.FC<PartDPenaltyCalculatorProps> = ({
  indDob,
  basePremium = 36.78,
}) => {
  type Mode = 'byBirthday' | 'byCoverageLoss';

  const [mode, setMode] = React.useState<Mode>('byBirthday');
  const [month, setMonth] = React.useState<string>('');
  const [year, setYear] = React.useState<string>('');
  const [monthsWithoutCoverage, setMonthsWithoutCoverage] = React.useState(0);
  const [penalty, setPenalty] = React.useState(0);
  const [yearInvalid, setYearInvalid] = React.useState(false);

  const resetCalc = () => {
    setMonthsWithoutCoverage(0);
    setPenalty(0);
    setYearInvalid(false);
  };

  const recalcIfPossible = (nextMonth: string, nextYear: string, nextMode: Mode) => {
    if (!nextMonth || nextYear.length !== 4 || yearInvalid) return;

    if (nextMode === 'byBirthday') {
      const { months, penalty } = calculateByBirthday(nextMonth, nextYear, basePremium);
      setMonthsWithoutCoverage(months);
      setPenalty(penalty);
    } else {
      const { months, penalty } = calculateByLoss(nextMonth, nextYear, basePremium);
      setMonthsWithoutCoverage(months);
      setPenalty(penalty);
    }
  };

  // Prefill from DOB when in "byBirthday" mode (replaces calcBybday)
  React.useEffect(() => {
    if (mode !== 'byBirthday') return;

    if (!indDob || indDob === '12/31/1969') {
      setMonth('');
      setYear('');
      resetCalc();
      return;
    }

    const parts = indDob.split('/');
    if (parts.length !== 3) {
      setMonth('');
      setYear('');
      resetCalc();
      return;
    }

    const [mm, , yyyy] = parts;
    setMonth(mm);
    setYear(yyyy);

    const { months, penalty } = calculateByBirthday(mm, yyyy, basePremium);
    setMonthsWithoutCoverage(months);
    setPenalty(penalty);
  }, [mode, indDob, basePremium]);

  const handleModeChange = (newMode: Mode) => {
    setMode(newMode);
    setMonth('');
    setYear('');
    resetCalc();
  };

  const handleMonthChange: React.ChangeEventHandler<HTMLSelectElement> = (e) => {
    const value = e.target.value;
    setMonth(value);
    recalcIfPossible(value, year, mode);
  };

  const handleYearChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    setYear(value);

    if (value.length === 4) {
      const numericYear = parseInt(value, 10);
      const currentYear = new Date().getFullYear();

      if (numericYear > currentYear) {
        setYearInvalid(true);
        return;
      } else {
        setYearInvalid(false);
      }
      recalcIfPossible(month, value, mode);
    } else {
      setYearInvalid(false);
    }
  };

  const labelMonth = mode === 'byBirthday' ? 'Birthday Month' : 'Last date of coverage';
  const labelYear = mode === 'byBirthday' ? 'Birthday Year' : 'Year';

  return (
    <div className="row gutter-2 mt-3">
      {/* Calculation method */}
      <div className="col-12 mb-3">
        <div className="form-check form-check-inline">
          <input
            className="form-check-input"
            type="radio"
            name="flexRadioDefault"
            id="flexRadioDefault1"
            checked={mode === 'byBirthday'}
            onChange={() => handleModeChange('byBirthday')}
          />
          <label className="form-check-label label" htmlFor="flexRadioDefault1">
            By Medicare eligibility date
          </label>
        </div>
        <div className="form-check form-check-inline">
          <input
            className="form-check-input"
            type="radio"
            name="flexRadioDefault"
            id="flexRadioDefault2"
            checked={mode === 'byCoverageLoss'}
            onChange={() => handleModeChange('byCoverageLoss')}
          />
          <label className="form-check-label label" htmlFor="flexRadioDefault2">
            By last date of coverage
          </label>
        </div>
      </div>

      {/* Inputs */}
      <div className="col-8 col-md-8 mb-2">
        <label className="label">{labelMonth}</label>
        <select
          className="form-control"
          id="penaltyMonth"
          value={month}
          onChange={handleMonthChange}
        >
          <option value="">Select month</option>
          <option value="01">January</option>
          <option value="02">February</option>
          <option value="03">March</option>
          <option value="04">April</option>
          <option value="05">May</option>
          <option value="06">June</option>
          <option value="07">July</option>
          <option value="08">August</option>
          <option value="09">September</option>
          <option value="10">October</option>
          <option value="11">November</option>
          <option value="12">December</option>
        </select>
      </div>

      <div className="col-4 col-md-4 mb-2">
        <label className="label">{labelYear}</label>
        <input
          type="text"
          className="form-control"
          id="penaltyYear"
          placeholder="Year"
          value={year}
          onChange={handleYearChange}
          maxLength={4}
        />
        <small className={`text-danger ${yearInvalid ? '' : 'hiddenStuff'}`} id="yearInvalid">
          Invalid year
        </small>
      </div>

      {/* Results */}
      <div className="col-12 col-md-12 mb-2 mt-4 text-center border-top pt-3">
        <h6>
          Estimated penalty for{' '}
          <b>
            <span id="mnthReturn">{monthsWithoutCoverage}</span>
          </b>{' '}
          months without coverage
        </h6>
      </div>
      <div className="col-12 col-md-12 mb-2 mt-2 text-center">
        <h2 className="text-primary">
          <b id="penaltyAmount">${penalty.toFixed(2)}</b>
        </h2>
        <h5 className="text-primary">per month</h5>
        <p className="text-primary">
          <i>In addition to monthly premium</i>
        </p>
      </div>

      {/* Disclosures */}
      <div className="col-12 col-md-12 mb-2 mt-4 text-center">
        <p>
          <i style={{ fontSize: '8pt' }}>
            The late enrollment penalty is calculated by multiplying 1% of the “national base
            beneficiary premium” (${basePremium.toFixed(2)} in 2025) times the number of full,
            uncovered months the beneficiary didn’t have Part D or other creditable coverage (but
            were eligible). The penalty amount is rounded to the nearest $0.10 and added to their
            monthly Part D premium. If eligible before 2006, calculations will start at 2006.
          </i>
        </p>
      </div>
    </div>
  );
};
